/**
 * CNES Service
 *
 * Service for managing CNES (Cadastro Nacional de Estabelecimentos de Saúde) data
 * Integrates with HospitalCNES database and CNES API
 *
 * Features:
 * - List hospitals from local CNES database with advanced filters
 * - Get hospital details by CNES code
 * - Import/update hospitals from CNES API
 * - Get states and municipalities lists
 * - Get statistics dashboard
 */

import apiClient from './apiClient';

export interface HospitalCNES {
  id: number;
  cnes: string;
  nome: string;
  cnpj: string | null;

  // Endereço
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cep: string | null;
  municipio: string;
  uf: string;
  codigo_municipio: string | null;

  // Contatos
  telefone: string | null;
  email: string | null;

  // Coordenadas
  latitude: string | null;
  longitude: string | null;

  // Classificação
  tipo_estabelecimento: string | null;
  subtipo_estabelecimento: string | null;
  natureza_juridica: string | null;
  esfera_administrativa: string | null;

  // Capacidade
  qtd_leitos_sus: number | null;
  qtd_leitos_nao_sus: number | null;
  qtd_leitos_total: number | null;
  qtd_leitos_uti: number | null;
  qtd_salas_cirurgicas: number | null;
  qtd_ambulatorios: number | null;

  // Serviços
  tem_urgencia: boolean;
  tem_ambulatorio: boolean;
  tem_internacao: boolean;
  tem_atendimento_sus: boolean;
  tem_centro_cirurgico: boolean;
  tem_centro_obstetrico: boolean;
  tem_uti: boolean;
  tem_sadt: boolean;

  // Gestão
  gestao: string | null;
  vinculo_sus: string | null;
  retencao: string | null;

  // Administrativo
  diretor: string | null;
  cpf_diretor: string | null;

  // Status
  data_atualizacao_cnes: string | null;
  situacao: string;
  motivo_desativacao: string | null;

  // JSON Fields
  servicos_especializados: any[] | null;
  equipamentos: any[] | null;
  habilitacoes: any[] | null;
  profissionais: any | null;

  // Controle
  imported_by: number | null;
  imported_at: string;
  updated_at: string;
  ativo: boolean;
}

export interface HospitalCNESInput {
  cnes: string;
  nome: string;
  cnpj?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cep?: string | null;
  municipio: string;
  uf: string;
  codigo_municipio?: string | null;
  telefone?: string | null;
  email?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  tipo_estabelecimento?: string | null;
  subtipo_estabelecimento?: string | null;
  natureza_juridica?: string | null;
  esfera_administrativa?: string | null;
  qtd_leitos_sus?: number | null;
  qtd_leitos_nao_sus?: number | null;
  qtd_leitos_total?: number | null;
  qtd_leitos_uti?: number | null;
  qtd_salas_cirurgicas?: number | null;
  qtd_ambulatorios?: number | null;
  tem_urgencia?: boolean;
  tem_ambulatorio?: boolean;
  tem_internacao?: boolean;
  tem_atendimento_sus?: boolean;
  tem_centro_cirurgico?: boolean;
  tem_centro_obstetrico?: boolean;
  tem_uti?: boolean;
  tem_sadt?: boolean;
  gestao?: string | null;
  vinculo_sus?: string | null;
  retencao?: string | null;
  diretor?: string | null;
  cpf_diretor?: string | null;
  data_atualizacao_cnes?: string | null;
  situacao?: string;
  motivo_desativacao?: string | null;
  servicos_especializados?: any[] | null;
  equipamentos?: any[] | null;
  habilitacoes?: any[] | null;
  profissionais?: any | null;
}

export interface CNESListFilters {
  uf?: string;
  municipio?: string;
  search?: string;
  tipo_estabelecimento?: string;
  tem_atendimento_sus?: boolean;
  tem_uti?: boolean;
  tem_urgencia?: boolean;
  qtd_leitos_min?: number;
  qtd_leitos_max?: number;
  situacao?: string;
  ativo?: boolean;
  page?: number;
}

export interface Estado {
  uf: string;
  nome: string;
  qtd_hospitais: number;
}

export interface Municipio {
  codigo: string;
  nome: string;
  uf: string;
  qtd_hospitais: number;
}

export interface CNESStats {
  total_hospitais: number;
  por_uf: Array<{ uf: string; count: number }>;
  leitos: {
    total: number;
    sus: number;
    nao_sus: number;
    uti: number;
    media_por_hospital: number;
  };
  servicos: {
    com_sus: number;
    com_uti: number;
    com_urgencia: number;
    com_centro_cirurgico: number;
    com_centro_obstetrico: number;
  };
  tipos: Array<{ tipo: string; count: number }>;
}

export interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  errors: Array<{ cnes: string; error: string }>;
  total_processed: number;
}

/**
 * List hospitals from local CNES database with filters
 */
export async function listCNESLocal(filters?: CNESListFilters): Promise<{
  count: number;
  items: HospitalCNES[];
}> {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.uf) params.append('uf', filters.uf);
    if (filters.municipio) params.append('municipio', filters.municipio);
    if (filters.search) params.append('search', filters.search);
    if (filters.tipo_estabelecimento) params.append('tipo_estabelecimento', filters.tipo_estabelecimento);
    if (filters.tem_atendimento_sus !== undefined) params.append('tem_atendimento_sus', String(filters.tem_atendimento_sus));
    if (filters.tem_uti !== undefined) params.append('tem_uti', String(filters.tem_uti));
    if (filters.tem_urgencia !== undefined) params.append('tem_urgencia', String(filters.tem_urgencia));
    if (filters.qtd_leitos_min !== undefined) params.append('qtd_leitos_min', String(filters.qtd_leitos_min));
    if (filters.qtd_leitos_max !== undefined) params.append('qtd_leitos_max', String(filters.qtd_leitos_max));
    if (filters.situacao) params.append('situacao', filters.situacao);
    if (filters.ativo !== undefined) params.append('ativo', String(filters.ativo));
    if (filters.page) params.append('page', String(filters.page));
  }

  const response = await apiClient.get(`/cnes/local?${params.toString()}`);
  return response.data;
}

/**
 * Get hospital details by CNES code
 */
export async function getCNESByCode(cnesCode: string): Promise<HospitalCNES> {
  const response = await apiClient.get(`/cnes/local/${cnesCode}`);
  return response.data;
}

/**
 * Get list of states with hospital counts
 */
export async function listEstados(): Promise<Estado[]> {
  const response = await apiClient.get('/cnes/estados');
  return response.data;
}

/**
 * Get list of municipalities by state
 */
export async function listMunicipios(uf: string): Promise<Municipio[]> {
  const response = await apiClient.get(`/cnes/municipios?uf=${uf}`);
  return response.data;
}

/**
 * Import multiple hospitals (bulk import)
 */
export async function importCNESBulk(hospitais: HospitalCNESInput[]): Promise<ImportResult> {
  const response = await apiClient.post('/cnes/import', hospitais);
  return response.data;
}

/**
 * Import single hospital
 */
export async function importCNESSingle(hospital: HospitalCNESInput): Promise<ImportResult> {
  const response = await apiClient.post('/cnes/import-single', hospital);
  return response.data;
}

/**
 * Get CNES statistics dashboard
 */
export async function getCNESStats(): Promise<CNESStats> {
  const response = await apiClient.get('/cnes/stats');
  return response.data;
}

/**
 * Format CNES code (ensure 7 digits)
 */
export function formatCNESCode(code: string): string {
  return code.padStart(7, '0');
}

/**
 * Format address from hospital data
 */
export function formatAddress(hospital: HospitalCNES): string {
  const parts = [
    hospital.logradouro,
    hospital.numero,
    hospital.bairro,
    hospital.municipio,
    hospital.uf,
    hospital.cep
  ].filter(Boolean);

  return parts.join(', ');
}

/**
 * Get capacity description
 */
export function getCapacityDescription(hospital: HospitalCNES): string {
  const leitos = hospital.qtd_leitos_total || 0;
  const uti = hospital.qtd_leitos_uti || 0;
  return `${leitos} leitos totais, ${uti} UTI`;
}

/**
 * Check if hospital has beds
 */
export function hasLeitos(hospital: HospitalCNES): boolean {
  return (hospital.qtd_leitos_total || 0) > 0;
}

export default {
  listCNESLocal,
  getCNESByCode,
  listEstados,
  listMunicipios,
  importCNESBulk,
  importCNESSingle,
  getCNESStats,
  formatCNESCode,
  formatAddress,
  getCapacityDescription,
  hasLeitos
};
